import { SortOrder, areOptionsEqual, } from '../../Components/SelectionList/index.js';
import { COMMA } from './constants.js';
import {} from './types.js';
export const getOptionLabel = (option, formatOptionLabel) => formatOptionLabel?.(option) ?? option.label;
export const getOptionText = (option, formatOptionLabel, getOptionTextFn) => {
    if (getOptionTextFn) {
        return getOptionTextFn(option);
    }
    // If formatOptionLabel returns a string, use it; otherwise fall back to option.label
    const formatted = formatOptionLabel?.(option);
    return typeof formatted === 'string' ? formatted : option.label;
};
export const createOptionValueSet = (options) => new Set(options.map((option) => option.value));
export const filterOptionsExcluding = (options, excludeSet) => options.filter((option) => !excludeSet.has(option.value));
export const filterOptionsByString = (options, searchString, formatOptionLabel, getOptionTextFn) => {
    if (!options.length) {
        return [];
    }
    if (!searchString.trim()) {
        return options;
    }
    const searchTerms = searchString
        .toLowerCase()
        .split(COMMA)
        .map((term) => term.trim())
        .filter((term) => term.length > 0);
    return options.filter((option) => {
        const textContent = getOptionText(option, formatOptionLabel, getOptionTextFn).toLowerCase();
        return searchTerms.some((term) => textContent.includes(term));
    });
};
export const sortOptions = (options, order, formatOptionLabel, getOptionTextFn) => {
    if (!options?.length) {
        return [];
    }
    if (order === SortOrder.ASC) {
        return [...options].sort((a, b) => getOptionText(a, formatOptionLabel, getOptionTextFn).localeCompare(getOptionText(b, formatOptionLabel, getOptionTextFn)));
    }
    return [...options].sort((a, b) => getOptionText(b, formatOptionLabel, getOptionTextFn).localeCompare(getOptionText(a, formatOptionLabel, getOptionTextFn)));
};
/** The comma-separated entries of the inline textarea, trimmed, empty ones dropped. */
export const splitInlineValue = (value) => value
    .split(COMMA)
    .map((token) => token.trim())
    .filter((token) => token.length > 0);
/** How an inline entry is compared: case and surrounding spaces do not count. */
export const toMatchKey = (text) => text.trim().toLowerCase();
/** A text two different options answer to names neither of them, so it is dropped rather than guessed. */
const addUnambiguous = (map, ambiguous, key, option) => {
    const existing = map.get(key);
    if (existing && !areOptionsEqual(existing, option)) {
        ambiguous.add(key);
        map.delete(key);
    }
    else if (!ambiguous.has(key)) {
        map.set(key, option);
    }
};
/**
 * Resolves the entries of the inline textarea to options, in the order they were typed and each option
 * once. An entry matches the text an option is shown as, then its raw label, then one of its aliases.
 * `sources` may repeat an option — the loaded page and the current selection overlap — which is not an
 * ambiguity.
 */
export const matchInlineTokens = (tokens, sources, formatOptionLabel, getOptionTextFn, getOptionAliases) => {
    const formattedMap = new Map();
    const labelMap = new Map();
    const aliasMap = new Map();
    const ambiguousLabels = new Set();
    const ambiguousAliases = new Set();
    for (const option of sources) {
        const formatted = toMatchKey(getOptionText(option, formatOptionLabel, getOptionTextFn));
        formattedMap.set(formatted, option);
        const rawLabel = toMatchKey(option.label);
        if (rawLabel !== formatted) {
            addUnambiguous(labelMap, ambiguousLabels, rawLabel, option);
        }
        for (const alias of getOptionAliases?.(option) ?? []) {
            addUnambiguous(aliasMap, ambiguousAliases, toMatchKey(alias), option);
        }
    }
    const seenValues = new Set();
    const seenUnmatchedKeys = new Set();
    return tokens.reduce((acc, token) => {
        const key = toMatchKey(token);
        const option = formattedMap.get(key) ?? labelMap.get(key) ?? aliasMap.get(key);
        if (!option) {
            // An entry typed twice is reported once.
            if (!seenUnmatchedKeys.has(key)) {
                seenUnmatchedKeys.add(key);
                acc.unmatched.push(token);
            }
        }
        else if (!seenValues.has(option.value)) {
            seenValues.add(option.value);
            acc.matched.push(option);
        }
        return acc;
    }, { matched: [], unmatched: [] });
};
